import React, { useRef, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import 'mapbox-gl/dist/mapbox-gl.css';
import './EasyMap.scss';
import { logger } from '../utils/logger';

const log = logger('EasyMap');

import { usePermissions } from '../context/PermissionContext';
import { AFF_FEATURE, AFF_READ_OP } from '../shared/affPermissions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudBolt,
  faFire,
  faSun,
  faKey,
  faHome,
  faUser,
  faRightFromBracket,
  faPlane,
} from '@fortawesome/free-solid-svg-icons';

import { useFusionAuth } from '@fusionauth/react-sdk';
import { announceStoredLiveWindow } from './layerDrawer/components/AffLiveWindowControl';
import { initializeMap } from './mapUtils';
import { registerFirePerimeterOverlay } from './perimeters/firePerimeterOverlay';
import { FirePerimeterLayerSync } from './perimeters/FirePerimeterLayerSync';
import { AffTrackLayerSync } from './liveLayers/AffTrackLayerSync';
import { saveViewport } from './mapViewportPersistence';
import { LayerService } from '../services/layerService';
import { getEnvironmentTitle } from '../services/configService';
import { layerPresetService } from '../services/layerPresetService';
// @deprecated - Old layer switcher system, replaced by Layer Drawer 2.0
// TODO: Remove these imports and components after Layer Drawer 2.0 is stable
// import LayerSwitcher from './mapcontrols/LayerSwitcher/LayerSwitcher';
// import TreeSwitcher from './mapcontrols/TreeSwitcher/TreeSwitcher';
// import type { LayerGroup } from './mapcontrols/TreeSwitcher/TreeSwitcher';
import ToolBoxContainer from './mapcontrols/ToolBox/ToolBoxContainer';
import SupportTool from './mapcontrols/SupportTool/SupportTool';
import ZoomLevel from './mapcontrols/ZoomLevel/ZoomLevel';
import ZoomControl from './mapcontrols/ZoomControl/ZoomControl';
import CoordinateDisplay from './mapcontrols/CoordinateDisplay';
import GetFeatureInfoControl from './mapcontrols/GetFeatureInfoControl/GetFeatureInfoControl';
import { SpotWxControl, SpotWxHandler } from './mapcontrols/SpotWxControl';
import MeasuringTool from './mapcontrols/MeasuringTool/MeasuringTool';
import VARMapTool from './mapcontrols/VARMapTool/VARMapTool';
import MapPrinting from './mapcontrols/MapPrinting/MapPrinting';
import { LegendControl } from './mapcontrols/LegendControl';
import Map3DToggle from './mapcontrols/Map3DToggle/Map3DToggle';
import MapContextMenu from './MapContextMenu';
import PasswordReset from '../components/userComponents/PasswordReset';
import UserEdit from '../components/userComponents/UserEdit';
import AppModal from '../components/common/AppModal';
import { PanelManagerProvider, usePanelManager } from '../components/panels/PanelManager';
// Import the layer provider getters - lazy initialization ensures config is loaded
// Provider singletons are imported and registered with LayerRegistry in
// LayerDrawerProvider — no React-side reference needed here anymore.

// The live-layer-buttons + .live-layer-divider styles live in
// liveLayers/LiveMapLayer.scss. Previously they loaded as a side-effect of
// importing LiveMapLayer; now that the React mounts are gone we still want
// the styles for the corner quick-toggle buttons defined below.
import './liveLayers/LiveMapLayer.scss';
import { ToolProvider, useTools, isToolKey, type ToolKey } from '../context/ToolContext';
import ToolHost from '../components/tools/ToolHost';
import TopMenuBar from '../components/navigation/TopMenuBar';
import { LayerDrawerProvider, LayerDrawer, LayerDrawerToggle, useLayerDrawer, LiveLayerDef, DrawerHeight } from './layerDrawer';
import { GetFeatureInfoCoordinator } from './services/GetFeatureInfoCoordinator';
import { PlacenameHandler } from './mapcontrols/PlacenameTool/PlacenameHandler';
import { useMessages } from '../components/common/AppMessages';

interface LayerDrawerWrapperProps {
  map: mapboxgl.Map;
}

// Live Weather + AFF are now loaded from the `layers` DB table as
// type='live' rows (migration 036). LayerDrawerProvider registers their
// providers with the LayerRegistry at map-set time. No runtime drawer
// registration needed here — the drawer reads from `layers` directly.
const LayerDrawerWrapper: React.FC<LayerDrawerWrapperProps> = ({ map }) => {
  const { setMap } = useLayerDrawer();
  const { userInfo } = useFusionAuth() as { userInfo?: { sub?: string } };

  useEffect(() => {
    if (!map) return;
    setMap(map);

    // #822 — setMap is what registers the live layer providers, so the window
    // the user chose last time is announced here, immediately after, while the
    // AFF provider is listening and before its first fetch. A child component
    // could not do this: its mount effect would run before this one.
    announceStoredLiveWindow(userInfo?.sub);
  }, [map, setMap, userInfo?.sub]);

  return (
    <>
      <LayerDrawerToggle />
      <LayerDrawer />
    </>
  );
};

// Corner quick-toggle buttons for Live Weather + AFF. Lookup is by stable
// providerId rather than UUID so the buttons work on every env regardless
// of which IDs the migration generated. Active styling reads directly off
// the drawer's `layers` array, so toggling either via this button or via
// the drawer keeps the other in sync automatically.
const LiveLayerQuickToggleButtons: React.FC = () => {
  const { layers, toggleLayer } = useLayerDrawer();
  const { canDo } = usePermissions();
  const findByProviderId = (id: string) =>
    layers.find(l => (l.config as any)?.providerId === id);
  const weather = findByProviderId('weather-stations');
  // The live aircraft layer is aff.read (#866).
  const aff = canDo(AFF_FEATURE, AFF_READ_OP) ? findByProviderId('aff') : undefined;

  // If the rows haven't been seeded yet (migration 036 hasn't run), render
  // nothing rather than show dead buttons.
  if (!weather && !aff) return null;

  return (
    <div className="live-layer-buttons">
      {weather && (
        <button
          onClick={() => toggleLayer(weather.id)}
          className={`control-button ${weather.enabled ? 'active' : ''}`}
          title={`${weather.enabled ? 'Hide' : 'Show'} Live Weather`}
        >
          <FontAwesomeIcon icon={faCloudBolt} />
        </button>
      )}
      {weather && aff && <div className="live-layer-divider" />}
      {aff && (
        <button
          onClick={() => toggleLayer(aff.id)}
          className={`control-button ${aff.enabled ? 'active' : ''}`}
          title={`${aff.enabled ? 'Hide' : 'Show'} Live Aircraft`}
        >
          <FontAwesomeIcon icon={faPlane} />
        </button>
      )}
    </div>
  );
};

const EasyMapContent: React.FC = () => {
  const { showMessage } = useMessages();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const [layerDrawerReady, setLayerDrawerReady] = useState(false);
  const { canDo } = usePermissions();
  const canReadLayers = canDo('layers', 'read');
  const canMeasure = canDo('tools', 'measurement');

  const [initialBackground, setInitialBackground] = useState<string | null>(null);
  const [showUserEditor, setShowUserEditor] = useState(false);
  const [ShowPasswordReset, setShowPasswordReset] = useState(false);
  const [featureInfoCoordinator, setFeatureInfoCoordinator] = useState<GetFeatureInfoCoordinator | null>(null);
  const [spotWxHandler, setSpotWxHandler] = useState<SpotWxHandler | null>(null);
  const [placenameHandler, setPlacenameHandler] = useState<PlacenameHandler | null>(null);
  const { addPanel } = usePanelManager();
  const { openTool } = useTools();
  const { setDrawerHeight, registry, map: contextMap, loadPreset, layers, loading } = useLayerDrawer();
  const { userInfo } = useFusionAuth();
  const userId = (userInfo as { sub?: string } | null | undefined)?.sub ?? null;
  // Seed from sessionStorage so the synchronous map init effect has a real
  // userId on first render. main.tsx pre-fetches /app/me before mount.
  const bootUserId = typeof window !== 'undefined'
    ? sessionStorage.getItem('em3:bootUserId')
    : null;
  const userIdRef = useRef<string | null>(userId ?? bootUserId);
  userIdRef.current = userId ?? bootUserId;

  // Map initialization - runs once on mount only
  // Using ref to track created map for proper cleanup
  const mapInstanceRef = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    // Prevent duplicate initialization
    if (mapInstanceRef.current) {
      log.debug('[EasyMap] Map already initialized, skipping');
      return;
    }

    const initialize = async () => {
      const layerService = new LayerService();

      // Step 1: Determine which background to use
      // Priority: localStorage (user preference) > database default
      let initialStyleUrl: string | undefined;
      let selectedBackgroundId: string | undefined;

      // Skip the layer fetch + background lookup entirely if the user lacks
      // layers.read. The map falls back to its hardcoded style and no custom
      // layers are loaded, which is the right behavior for a permission-less
      // viewer.
      if (canReadLayers) {
        try {
          // Check localStorage for user's last background preference.
          // Key is user-scoped to match LayerDrawerProvider's persistence.
          const layerStatesKey = userIdRef.current
            ? `em3-layer-states:${userIdRef.current}`
            : null;
          const persistedStates = layerStatesKey
            ? localStorage.getItem(layerStatesKey)
            : null;

          if (persistedStates) {
            const states = JSON.parse(persistedStates);
            // Fetch all layers to find which background is enabled in localStorage
            const allLayers = await layerService.getAllLayers();
            const backgrounds = allLayers.filter(l => l.category === 'backgrounds');

            // Find the background that's enabled in localStorage
            const enabledBg = backgrounds.find(bg => states[bg.id] === true);
            if (enabledBg) {
              selectedBackgroundId = enabledBg.id;

              // Use styleUrl if it's a mapbox-style type
              if (enabledBg.type === 'mapbox-style') {
                initialStyleUrl = enabledBg.config?.styleUrl || enabledBg.source_url;
              }
            }
          }

          // Fall back to database default if no user preference
          if (!selectedBackgroundId) {
            const bgResult = await layerService.getDefaultBackground();
            if (bgResult?.layer) {
              selectedBackgroundId = bgResult.layer.id;

              if (bgResult.layer.type === 'mapbox-style') {
                initialStyleUrl = bgResult.layer.config?.styleUrl || bgResult.layer.source_url;
              }
            }
          }
        } catch (err) {
          log.warn('[EasyMap] Could not determine background, using hardcoded fallback:', err);
        }
      }

      // Step 2: Initialize map with the determined style
      const { map: mapInstance, initialBackground } = await initializeMap(
        mapContainerRef.current,
        initialStyleUrl,
        userIdRef.current
      );

      // Store ref for cleanup and state for React
      mapInstanceRef.current = mapInstance;
      setMap(mapInstance);
      setInitialBackground(initialBackground);

      // Register fire-perimeter overlay listeners so Show/Hide Perimeter
      // works regardless of which fire layers are currently enabled.
      if (mapInstance) registerFirePerimeterOverlay(mapInstance);

      // Persist viewport on every move/zoom so refresh restores position.
      // Read userId from ref so post-login changes are picked up without re-init.
      if (mapInstance) {
        const persist = () => {
          const c = mapInstance.getCenter();
          saveViewport(userIdRef.current, {
            lng: c.lng,
            lat: c.lat,
            zoom: mapInstance.getZoom(),
          });
        };
        mapInstance.on('moveend', persist);
      }
    };

    initialize();

    // Cleanup: remove map on unmount
    return () => {
      if (mapInstanceRef.current) {
        log.debug('[EasyMap] Cleaning up map instance');
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []); // Empty deps - initialize once on mount only

  // Handle URL parameters for tool deep linking
  // This runs in a SEPARATE effect so it waits for the map to be ready
  useEffect(() => {
    // Don't process URL params until map is initialized
    if (!map) return;

    const tool = searchParams.get('tool');
    const fireId = searchParams.get('fireId');
    const tab = searchParams.get('tab');

    // Handle tool parameter — accepts camelCase (varExplorer) or kebab-case (var-explorer)
    // Valid tools are derived from the TOOL_KEYS array in ToolContext (single source of truth)
    if (tool) {
      const toCamelCase = (s: string) => s.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
      const toolKey = isToolKey(tool) ? tool : isToolKey(toCamelCase(tool)) ? toCamelCase(tool) as ToolKey : null;

      if (toolKey) {
        // Handle wildfire with fireId - open detail view instead of explorer
        if (toolKey === 'wildfire' && fireId) {
          openTool('wildfireDetail', { fireId: fireId ?? undefined, tab: tab ?? undefined });
        }
        // Handle wildfireDetail with optional fireId and tab
        else if (toolKey === 'wildfireDetail') {
          openTool('wildfireDetail', { fireId: fireId ?? undefined, tab: tab ?? undefined });
        }
        // Handle special payload for dutylogs
        else if (toolKey === 'dutylogs' && fireId) {
          openTool(toolKey, { fireNumber: fireId });
        }
        // Handle layers - open drawer directly to avoid race condition
        else if (toolKey === 'layers') {
          setDrawerHeight(DrawerHeight.FULL);
        }
        // All other tools — automatically supported via TOOL_KEYS
        else {
          openTool(toolKey);
        }
      }
    }

    // Also support legacy 'modal' parameter for backwards compatibility
    const modal = searchParams.get('modal');
    if (modal && !tool) {  // Only if tool param not already handled
      if (modal === 'user-edit') {
        openTool('userEdit');
      } else if (modal === 'password-reset') {
        openTool('passwordReset');
      }
    }
  }, [map, searchParams, openTool, setDrawerHeight]);

  // listen for map → UI events (context menu, etc.)
  useEffect(() => {

    // wildfire detail from the map (the one we wired in the layer)
    const wildfireDetailHandler = (e: any) => {
      const fireNumber = e.detail?.fireNumber || null;
      if (!fireNumber) return;
      // we *could* fetch here, but your tool registry can do it too
      openTool('wildfireDetail', { fireId: fireNumber });
    };
    window.addEventListener('show-wildfire-details', wildfireDetailHandler);

    // listen for dutylog explorer with crosslink (map → React)
    const enhancedDLEHandler = (e: any) => {
      const fireNumber = e.detail?.fireNumber;
      if (fireNumber) {
        // tell ToolHost to open DLE and run a fire_code crosslink search
        openTool('dutylogs', {
          fireNumber,
        });
      } else {
        // open empty explorer
        openTool('dutylogs');
      }
    };
    window.addEventListener('show-enhanced-dutylog-explorer', enhancedDLEHandler);

    // listen for layer drawer open requests
    const layerDrawerHandler = () => {
      setDrawerHeight(DrawerHeight.FULL);
    };
    window.addEventListener('intellifire:open-layer-drawer', layerDrawerHandler);

    // listen for my location requests
    const myLocationHandler = () => {
      if (!map) return;
      if (!navigator.geolocation) {
        log.warn('Geolocation is not supported by this browser');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          map.flyTo({
            center: [position.coords.longitude, position.coords.latitude],
            zoom: 14,
          });
        },
        (error) => {
          log.error('Geolocation error:', error.message);
          showMessage(`Unable to get your location: ${error.message}`, 'error');
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    };
    window.addEventListener('intellifire:my-location', myLocationHandler);

    return () => {
      window.removeEventListener('intellifire:open-layer-drawer', layerDrawerHandler);
      window.removeEventListener('intellifire:my-location', myLocationHandler);
      window.removeEventListener('show-enhanced-dutylog-explorer', enhancedDLEHandler);
      window.removeEventListener('show-wildfire-details', wildfireDetailHandler);
    };
  }, [searchParams, openTool, setDrawerHeight, map]);

  // NEW: watch the URL for ?modal=... and open the right modal
  useEffect(() => {
    const modal = searchParams.get('modal');

    if (modal === 'password-reset') {
      setShowPasswordReset(true);
    } else if (modal === 'user-edit') {
      setShowUserEditor(true);
    }
    // add more modal types here later
  }, [searchParams]);

  // #557 — restore a saved map from its permalink (?permalink=<id>). Runs once,
  // after the map has loaded and the layer list is available (so loadPreset can
  // match layer ids). loadPreset restores layers/order/opacity/basemap AND the
  // viewport. The param is stripped afterwards so a refresh is clean.
  // Deep-link-through-login is handled upstream (main.tsx stashes /map?* before
  // redirecting to login).
  const permalinkRestoredRef = useRef(false);

  useEffect(() => {
    const permalinkId = searchParams.get('permalink');
    // Gate on the initial layer load being COMPLETE (loading === false), so every
    // layer's registry instance exists and the provider's one-time "enable
    // pending layers" pass has been set in motion. Without this, a cold start
    // (login → full reload → /map?permalink=) runs loadPreset mid-initialisation
    // and some layers are missed or wiped by the basemap switch.
    if (
      !permalinkId || !contextMap || !registry || loading ||
      layers.length === 0 || permalinkRestoredRef.current
    ) {
      return;
    }

    permalinkRestoredRef.current = true;
    let cancelled = false;

    // Wait until the normal cold-start restore has SETTLED — i.e. every layer
    // the app intends to enable is actually enabled on the map — before letting
    // loadPreset take over as the authoritative state. Bounded so a stuck layer
    // can't hang the restore.
    const waitForLayersSettled = async () => {
      for (let i = 0; i < 40 && !cancelled; i++) {
        const allEnabled = layers
          .filter((l) => l.enabled)
          .every((l) => registry.get(l.id)?.isEnabled());
        if (allEnabled) return;
        await new Promise((r) => setTimeout(r, 100));
      }
    };

    const run = async () => {
      try {
        await waitForLayersSettled();
        if (cancelled) return;
        const preset = await layerPresetService.resolvePermalink(permalinkId);
        if (cancelled) return;
        await loadPreset(preset);
        log.debug('Restored saved map from permalink', { permalinkId });
      } catch (err) {
        log.error('Failed to restore saved map from permalink', err);
      } finally {
        // Strip ?permalink so a refresh doesn't re-trigger the restore.
        const url = new URL(window.location.href);
        url.searchParams.delete('permalink');
        window.history.replaceState({}, '', url.toString());
      }
    };

    if (contextMap.loaded()) {
      run();
    } else {
      contextMap.once('load', run);
    }

    return () => {
      cancelled = true;
      contextMap.off('load', run);
    };
  }, [searchParams, contextMap, registry, layers, loadPreset, loading]);

  // Initialize GetFeatureInfo coordinator when map and registry are ready
  // IMPORTANT: Use contextMap (from LayerDrawer context) not local map state
  // The registry is tied to contextMap, so coordinator must use the same map reference
  const coordinatorRef = useRef<GetFeatureInfoCoordinator | null>(null);

  useEffect(() => {
    if (!contextMap || !registry || coordinatorRef.current) {
      return;
    }

    const createCoordinator = () => {
      const coordinator = new GetFeatureInfoCoordinator(contextMap, registry);
      coordinatorRef.current = coordinator;
      setFeatureInfoCoordinator(coordinator);
      log.debug('GetFeatureInfo coordinator initialized');
    };

    // Wait for map to be fully loaded before creating coordinator
    // This ensures the canvas won't be swapped out from under us
    if (contextMap.loaded()) {
      createCoordinator();
    } else {
      contextMap.once('load', createCoordinator);
    }

    return () => {
      contextMap.off('load', createCoordinator);
      if (!contextMap && coordinatorRef.current) {
        coordinatorRef.current.destroy();
        coordinatorRef.current = null;
      }
    };
  }, [contextMap, registry]);

  // Initialize SpotWx handler when map is ready
  const spotWxHandlerRef = useRef<SpotWxHandler | null>(null);

  useEffect(() => {
    if (!contextMap || spotWxHandlerRef.current) {
      return;
    }

    const createHandler = () => {
      const handler = new SpotWxHandler(contextMap);
      spotWxHandlerRef.current = handler;
      setSpotWxHandler(handler);
      log.debug('SpotWx handler initialized');
    };

    // Wait for map to be fully loaded before creating handler
    if (contextMap.loaded()) {
      createHandler();
    } else {
      contextMap.once('load', createHandler);
    }

    return () => {
      contextMap.off('load', createHandler);
      if (!contextMap && spotWxHandlerRef.current) {
        spotWxHandlerRef.current.destroy();
        spotWxHandlerRef.current = null;
      }
    };
  }, [contextMap]);

  // Initialize Placename handler when map is ready
  const placenameHandlerRef = useRef<PlacenameHandler | null>(null);

  useEffect(() => {
    if (!contextMap || placenameHandlerRef.current) {
      return;
    }

    const createHandler = () => {
      const handler = new PlacenameHandler(contextMap);
      placenameHandlerRef.current = handler;
      setPlacenameHandler(handler);
      log.debug('Placename handler initialized');
    };

    if (contextMap.loaded()) {
      createHandler();
    } else {
      contextMap.once('load', createHandler);
    }

    return () => {
      contextMap.off('load', createHandler);
      if (!contextMap && placenameHandlerRef.current) {
        placenameHandlerRef.current.destroy();
        placenameHandlerRef.current = null;
      }
    };
  }, [contextMap]);

  // Environment style helper
  const getEnvironmentStyle = (env: string) => {
  const name = env?.toLowerCase();
  switch (name) {
    case 'prod':
    case 'production':
    return { color: '#2222222', isProd: true };
    case 'uat':
    case 'testing':
    return { color: '#c47206ff', isProd: false };
    case 'staging':
    return { color: '#0d9d0dff', isProd: false };
    case 'localdev':
    case 'development':
    case 'dev':
    return { color: '#cbcb0aff', isProd: false };
    default:
    return { color: '#6c0927ff', isProd: false }; // default dark red
    }
  };

  const envTitle = getEnvironmentTitle();
  const { color: environmentColor, isProd } = getEnvironmentStyle(envTitle);
  log.debug('env style →', {
    raw: envTitle,
    environmentColor,
    isProd,
  });

  // @deprecated - Old layer group configuration, replaced by Layer Drawer 2.0
  // TODO: Remove after Layer Drawer 2.0 is stable
  // const groups: { [groupName: string]: LayerGroup } = {
  //   'NWT Fire History': { type: 'checkbox', layers: [] },
  //   'Sat Detection': { type: 'checkbox', layers: [] },
  //   'WBNP Data': { type: 'checkbox', layers: [] },
  //   Weather: { type: 'checkbox', layers: [], subgroups: { rain: { type: 'radio', layers: [] } } },
  //   Lightning: { type: 'radio', layers: [] },
  //   Modelling: { type: 'checkbox', layers: [] },
  //   'Environment Canada': { type: 'checkbox', layers: [] },
  // };


  return (
    <div className="map-wrapper">
      <header
        style={{
          padding: '0px',
          background: '#222',
          color: '#fff',
          borderBottom: `5px solid ${environmentColor}`,
          }}
       >
        <TopMenuBar config={{ main: [], account: [] }} />
      </header>

      {/* @deprecated - Old TreeSwitcher, replaced by Layer Drawer 2.0 */}
      {/* TODO: Remove after Layer Drawer 2.0 is stable */}
      {/* {map && initialBackground && (
        <TreeSwitcher map={map} initialBackground={initialBackground} groups={groups} />
      )} */}
      <div ref={mapContainerRef} className="map-container">
        {map && <CoordinateDisplay map={map} />}
        {map && <MapContextMenu map={map} coordinator={featureInfoCoordinator} spotWxHandler={spotWxHandler} placenameHandler={placenameHandler} />}
      </div>

      <div style={{ position: 'absolute', top: '60px', left: '10px' }}>
        {map && <ZoomLevel map={map} />}
        {map && <ZoomControl map={map} />}
        {/* Consolidated into MapContextMenu — right-click to access */}
        {/* <GetFeatureInfoControl coordinator={featureInfoCoordinator} /> */}
        {/* <SpotWxControl handler={spotWxHandler} /> */}
        {map && canMeasure && <MeasuringTool map={map} />}
        {/* VARMapTool listens for intellifire:var-create to open the create form.
            Mount only if the user can actually create — otherwise the listener
            is dead weight a determined user could trigger from devtools. */}
        {map && canDo('var', 'create') && <VARMapTool map={map} hidden />}
        <ToolBoxContainer map={map} />

        {map && <MapPrinting map={map} />}
        {/*
          Quick-toggle buttons for Live Weather + AFF. Post-migration 036
          these layers are type='live' DB rows; we look them up by their
          stable providerId and drive their enabled state through the same
          toggleLayer() the drawer uses. Active styling stays in sync with
          drawer toggles automatically because `layers` is the same array.
        */}
        {canReadLayers && <LiveLayerQuickToggleButtons />}
      </div>

      {/* 3D rotation toggle - above layer drawer toggle */}
      {map && <Map3DToggle map={map} />}

      {/* Layer Drawer 2.0 - Testing Integration */}
      {map && <LayerDrawerWrapper map={map} />}

      {/* Dynamic Legend Control - positioned bottom-right */}
      {map && <LegendControl />}

      {/* Modal for PasswordReset */}
      {ShowPasswordReset && (
        <AppModal isOpen onClose={() => setShowPasswordReset(false)} width={400}>
          <PasswordReset />
          <button className="close-button" onClick={() => setShowPasswordReset(false)}>
            Close
          </button>
        </AppModal>
      )}

      {/* Modal for UserEdit */}
      {showUserEditor && (
        <AppModal isOpen onClose={() => setShowUserEditor(false)} width={400}>
          <UserEdit />
          <button className="close-button" onClick={() => setShowUserEditor(false)}>
            Close
          </button>
        </AppModal>
      )}
    </div>
  );
};

const EasyMap: React.FC = () => {
  return (
    <PanelManagerProvider>
      <LayerDrawerProvider>
        <ToolProvider>
          <EasyMapContent />
          <ToolHost />
          <FirePerimeterLayerSync />
          <AffTrackLayerSync />
        </ToolProvider>
      </LayerDrawerProvider>
    </PanelManagerProvider>
  );
};

export default EasyMap;
